/* eslint no-restricted-syntax: 0, guard-for-in: 0, @typescript-eslint/no-non-null-assertion: 0 */
/**
 * This module exports pure functions for manipulating numbers.
 */

import { isNumber, isString } from 'lodash';

/**
 * Rounds down to the given number of significant figures. Truncates anything beyond
 * the decimal place, then rounds down to the desired number of significant figures.
 * It is used in places where we want to give ballparks. If we rounded up, it would
 * give the impression that the metric actually went that high.
 *
 * Example when rounding to 2 digits: 12999 -> 12000, 12.999 -> 12, 0.12999 -> 0
 *
 * @param num        Number to round.
 * @param digits     Number of significant digits
 *
 * @returns The input rounded down to its most significant digits before the decimal place.
 */
export const roundDownToSigFigs = (num: number, digits = 2): number => {
  let rounded = Math.floor(num);
  const numDigits: number = Number(rounded.toFixed(0).length) - digits;
  if (numDigits > 0) {
    const divisor = 10 ** numDigits;
    rounded = Math.floor(rounded / divisor) * divisor;
  }
  return rounded;
};

/**
 * Adds commas to a number.
 *
 * @param num                    number to format with commas
 * @param min                    if num is less than this, return it as is
 * @param fractionDigits         Option to set number of decimal places. Defaults to 0. Use `-1` to leave decimal places untouched
 *
 * @returns number formatted with commas
 */
export const addCommas = (num: number, min?: number, fractionDigits = 0): string => {
  const digits = fractionDigits === -1 ? undefined : fractionDigits;
  if (!min || num >= min) {
    return num.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
  }
  return num.toFixed(digits);
};

/**
 * Returns true if the given value is a number, or a string that represents a number.
 */
export const isNumeric = (value: string | number | undefined): boolean =>
  (isNumber(value) || isString(value)) && value !== '' && !Number.isNaN(Number(value));

export enum AbbreviateNumberFormat {
  NONE = 'NONE',
  K = 'K',
  M = 'M',
  B = 'B',
  KB = 'KB',
  MB = 'MB',
  GB = 'GB',
  TB = 'TB',
  PB = 'PB',
  AUTO = 'AUTO',
  AUTOBYTE = 'AUTOBYTE',
  commas = 'commas',
}

/**
 * Converts big numbers into shorter, abbreviated versions.
 *
 * If the incoming value is 10,000,000,000 (10 billion), these formats produce:
 *
 * 'B' - billions  => 10B
 * 'M' - millions  => 10,000M
 * 'K' - thousands => 10,000,000K
 *
 * 'auto' - examines the number and picks the best fit, so => 10B
 *
 * If it's anything else, we just insert commas into the original value.
 *
 * @param value  A number to be formatted
 * @param format A string format: "K" | "M" | "B" | "KB" | "MB" | "GB" | "TB" | "PB" | "AUTO" | "AUTOBYTE" | "commas"
 * @param fixedPrecision A number of digits after the dot
 * @param addSpace if true, add a space between the number and the unit
 */
export const abbreviateNumber = (
  value: number,
  format = AbbreviateNumberFormat.AUTO,
  fixedPrecision?: number,
  addSpace = false,
): string => {
  if (format === AbbreviateNumberFormat.NONE) {
    if (fixedPrecision !== undefined) {
      return value.toFixed(fixedPrecision);
    }

    return value.toString();
  }

  const D = 10;

  // Scalyr units above GB use 1000 as multiplier
  const byteFormats: Partial<Record<AbbreviateNumberFormat, number>> = {
    PB: 2 ** 30 * 1000 * 1000,
    TB: 2 ** 30 * 1000,
    GB: 2 ** 30,
    MB: 2 ** 20,
    KB: 2 ** 10,
  };

  const numFormats: Partial<Record<AbbreviateNumberFormat, number>> = {
    B: 10 ** 9,
    M: 10 ** 6,
    K: 10 ** 3,
  };

  const num = Math.floor(value);
  const space = addSpace ? ' ' : '';

  if (byteFormats[format]) {
    return addCommas(value / byteFormats[format]!, 10000, fixedPrecision) + space + format;
  }

  if (format === AbbreviateNumberFormat.AUTOBYTE) {
    const entries = Object.entries(byteFormats);

    // We have to use for in syntax to ensure early return
    for (const i in entries) {
      const [fmt, size] = entries[i];
      if (num >= size!) {
        return addCommas(value / size!, 10000, fixedPrecision) + space + fmt;
      }
    }
  }

  if (numFormats[format]) {
    return addCommas(value / numFormats[format]!, 10000, fixedPrecision) + space + format;
  }

  if (format === AbbreviateNumberFormat.AUTO) {
    const entries = Object.entries(numFormats);

    for (const i in entries) {
      const [fmt, size] = entries[i];
      if (num >= size!) {
        return addCommas(value / size!, 10000, fixedPrecision) + space + fmt;
      }
    }
  }

  return addCommas(value > D ? num : Math.floor(value * 100.0) / 100.0, 10000); // max 2 decimals
};

/**
 * Useful in building mock data
 */
export function getRandomNumber(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * Format a number as currency (dollars). Always show two decimal places.
 *
 * @param value    number to format
 */
export function formatMoney(value: number): string {
  return '$' + addCommas(Number(value.toFixed(2)), -1, 2);
}
